import { JDSPaginationProps, Pagination } from './generated';
import { getNumbers, useTokenValues } from '@jds/react-engine';

import { useMemo } from 'react';
import { useSelected } from '../../hooks/useSelected';

const legacyKinds = ['truncated'] as const;
type legacyKindsType = (typeof legacyKinds)[number];

export interface PaginationProps
  extends Omit<JDSPaginationProps, 'kind' | 'onSelect'> {
  /**
   * Variant of pagination component
   *
   * `truncated` is deprecated
   */
  kind?: JDSPaginationProps['kind'] | legacyKindsType;
  /**
   * This function is invoked everytime a page selector is clicked
   */
  onPageChange?: (page: number) => {};
}

export interface JdsPaginationItems {
  state?: string;
  text?: string;
}

const ellipsis: JdsPaginationItems = {
  state: 'ellipsis',
  text: '...'
};

export function getTruncatedFiniteItems(
  length: number,
  page: number
): JdsPaginationItems[] {
  if (page <= 4) {
    const start: JdsPaginationItems[] = Array.from({ length: 5 }).map(
      (_, index) => ({
        state: index + 1 === page ? 'selected' : 'default',
        text: `${index + 1}`
      })
    );
    const end: JdsPaginationItems = {
      state: page === length ? 'selected' : 'default',
      text: length.toString()
    };
    return [...start, ellipsis, end];
  } else if (page >= length - 3) {
    const start: JdsPaginationItems = {
      state: page === 1 ? 'selected' : 'default',
      text: '1'
    };
    const end: JdsPaginationItems[] = Array.from({ length: 5 }).map(
      (_, index) => ({
        state: page === index + length - 4 ? 'selected' : 'default',
        text: `${index + length - 4}`
      })
    );
    return [start, ellipsis, ...end];
  }
  const start: JdsPaginationItems = {
    state: page === 1 ? 'selected' : 'default',
    text: '1'
  };
  const end: JdsPaginationItems = {
    state: page === length ? 'selected' : 'default',
    text: length.toString()
  };
  const mid: JdsPaginationItems[] = Array.from({ length: 3 }).map(
    (_, index) => ({
      state: index + page - 1 === page ? 'selected' : 'default',
      text: `${index + page - 1}`
    })
  );
  return [start, ellipsis, ...mid, ellipsis, end];
}

export function getInfiniteItems(page: number): JdsPaginationItems[] {
  if (page <= 5) {
    const start: JdsPaginationItems[] = Array.from({ length: 6 }).map(
      (_, index) => ({
        state: index + 1 === page ? 'selected' : 'default',
        text: `${index + 1}`
      })
    );
    return [...start, ellipsis];
  }
  const start: JdsPaginationItems = {
    state: page === 1 ? 'selected' : 'default',
    text: '1'
  };
  const mid: JdsPaginationItems[] = Array.from({ length: 4 }).map(
    (_, index) => ({
      state: index + page - 2 === page ? 'selected' : 'default',
      text: `${index + page - 2}`
    })
  );
  return [start, ellipsis, ...mid, ellipsis];
}

export function getBasicItems(
  length: number,
  page: number
): JdsPaginationItems[] {
  return Array.from({ length }).map((_, index) => ({
    text: `${index + 1}`,
    state: index + 1 === page ? 'selected' : 'default'
  }));
}

const range = (start: number, end: number): number[] => {
  const length = end - start + 1;
  return Array.from({ length }, (_, i) => start + i);
};

export const showPages = (totalPages: number, page: number, kind: string) => {
  let output = [] as any;

  if (!totalPages || totalPages < 0) {
    if (kind === 'basic') {
      if (page <= 4) {
        output = range(1, page);
      } else {
        output = [1, '...', page - 1, page];
      }
      return [...output, '...'];
    }
    if (kind === 'compact') {
      output = [page];
    }
  } else {
    output = range(1, totalPages <= 4 ? totalPages : 4);
    if (kind === 'basic') {
      if (totalPages > 4) {
        return [...output, '...', totalPages];
      }
    }
    if (kind === 'compact') {
      output = [page, '/', totalPages];
    }
  }

  return output;
};

export const showLastPageButton = (totalPages: number) => {
  if (!totalPages || totalPages < 0) return false;

  return true;
};

export const withLogic =
  (
    Component: React.ComponentType<PaginationProps>
  ): React.FC<PaginationProps> =>
  (props: PaginationProps) => {
    const { page, totalPages = 0, onPageChange, kind, ...rest } = props;
    const { index, onClick, updateIndex } = useSelected(page);

    const handlePrev = () => {
      if (index !== 0) {
        updateIndex(index - 1);
        onPageChange?.(index - 1);
      }
    };

    const handleNext = () => {
      if (index !== totalPages) {
        updateIndex(index + 1);
        onPageChange?.(index + 1);
      }
    };

    const items = () => {
      const length = totalPages ?? 0;
      const page = index ?? 0;
      if (length > 0 && length <= 7) {
        return getBasicItems(length, page);
      } else if (length > 7) {
        // truncated logic
        return getTruncatedFiniteItems(length, page);
      } else if (length < 1) {
        return getInfiniteItems(page);
      }
      return [];
    };
    const tokens = useTokenValues();
    const language = tokens.lang;
    const numeralList = useMemo(() => {
      return getNumbers(language);
    }, [language]);
    const getLocalNumber = (number: number) => {
      const numString = number.toString();
      return numString.replace(
        /\d/g,
        (num) => numeralList[parseInt(num, 10)] ?? num
      );
    };
    const currentItems = items().map((item, i) => ({
      ...item,
      text: item.text !== '...' ? getLocalNumber(Number(item.text)) : item.text,
      onSelect: () => {
        const { text } = item;
        if (text !== '...') {
          const currentPage = parseInt(text);
          onClick(currentPage);
          onPageChange?.(currentPage);
        }
      }
    }));

    const firstButtonState = index === 1;
    const lastButtonState = index === totalPages;
    console.log(lastButtonState, 205);
    return (
      <Component
        {...{
          ...rest,
          _items: currentItems,
          kind,
          onPrevious: handlePrev,
          onNext: handleNext,
          _helperText: `${getLocalNumber(index)} / ${getLocalNumber(
            totalPages
          )}`,
          onFirst: () => updateIndex(1),
          onLast: () => updateIndex(totalPages),
          _previousDisabled: firstButtonState,
          _firstDisabled: firstButtonState,
          _lastDisabled: lastButtonState,
          _nextDisabled: lastButtonState
        }}
      />
    );
  };
